import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import '../../style/scss/modals.scss';
import '../../style/scss/environmentDeletion.scss';

/*
 * EnvironmentDeleteModal.tsx
 * Modal for the superuser environment deletion, opened from the trash
 * icon in the environment list. The exact environment name must be
 * typed before the deletion starts, after which the progress is shown
 * in the same modal. Reopening the modal during a running deletion
 * shows the progress again, a stuck or failed deletion can be restarted.
 * The demo environment list reuses the modal with its own endpoints.
 */

interface EnvironmentDeletionResponse {
    deletion_status: string;
    session_relinked: boolean;
    admin_environment_hash: string;
}

interface EnvironmentDeletionStatus {
    deletion_status: string;
    deletion_progress: number;
    is_running: boolean;
}

interface EnvironmentDeleteModalProps {
    environmentId: number;
    environmentName: string;
    onFinished?: () => void;
    deleteEndpoint?: string;
    statusEndpoint?: string;
}

const EnvironmentDeleteModal: React.FC<EnvironmentDeleteModalProps> = ({
    environmentId,
    environmentName,
    onFinished,
    deleteEndpoint = 'delete_environment_as_superuser',
    statusEndpoint = 'get_environment_deletion_status_as_superuser'
}) => {
    const { t } = useTranslation();
    const { revealModal, closeModal } = useModal();
    const { handleLogout, handleSwitchEnvironment } = useAuthContext();
    const [status, setStatus] = useState<EnvironmentDeletionStatus | null>(null);
    const [confirmationName, setConfirmationName] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Fetch the current deletion status on mount and reveal the modal
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const response = await fetchStatusFromBackend();
                setStatus(response);

                // Resume the progress polling when the deletion runs
                if (response.deletion_status === 'deleting' && response.is_running) {
                    startPolling();
                }
            } catch (error) {
                console.error('Fetching the environment deletion status failed', error);
                setStatus({ deletion_status: 'idle', deletion_progress: 0, is_running: false });
            } finally {
                revealModal();
            }
        };
        fetchStatus();

        // Stop polling when the modal unmounts
        return () => stopPolling();
    }, []);

    // Fetch the deletion status of the environment
    const fetchStatusFromBackend = async (): Promise<EnvironmentDeletionStatus> => {
        return await fetchData({ apiUrl: `${statusEndpoint}/${environmentId}`, handleLogout });
    };

    // Stop the polling interval
    const stopPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    // Poll the deletion status every one and a half seconds while the deletion runs
    const startPolling = () => {
        stopPolling();
        pollingRef.current = setInterval(async () => {
            try {
                const response = await fetchStatusFromBackend();
                setStatus(response);

                // Stop polling when the deletion is finished, failed or lost
                if (response.deletion_status !== 'deleting' || !response.is_running) {
                    stopPolling();
                    if (response.deletion_status === 'deleted' && onFinished) onFinished();
                }
            } catch (error) {
                console.error('Fetching the environment deletion status failed', error);
            }
        }, 1500);
    };

    // The deletion can only be started when the typed name matches exactly
    const nameMatches = confirmationName === environmentName;

    // Translate the blocker keys of the backend into one message
    const getBlockerMessage = (blockers: string[]) => {
        return blockers.map((blocker) => t(`environment.deletion.blockers.${blocker}`)).join(' ');
    };

    // Start the deletion in the backend
    const handleDelete = async () => {
        if (!nameMatches || loading) return;
        setLoading(true);
        setErrorMessage(null);

        try {
            // Post the deletion request with the typed confirmation name
            const response = await saveData({
                apiUrl: `${deleteEndpoint}/${environmentId}`,
                method: 'post',
                data: { confirmation_name: confirmationName }
            });
            const data = response?.data as EnvironmentDeletionResponse | undefined;

            // A relinked session reloads the app inside the admin environment
            if (data?.session_relinked && data.admin_environment_hash) {
                handleSwitchEnvironment(data.admin_environment_hash);
                return;
            }

            // Show the progress in this modal and start polling
            setStatus({ deletion_status: 'deleting', deletion_progress: 0, is_running: true });
            setConfirmationName('');
            startPolling();
        } catch (error: any) {
            // Show the blockers or the general error of the backend
            const responseData = error?.response?.data;
            if (responseData?.blockers?.length) {
                setErrorMessage(getBlockerMessage(responseData.blockers));
            } else if (responseData?.confirmation_name) {
                setErrorMessage(t(responseData.confirmation_name));
            } else if (responseData?.general) {
                setErrorMessage(t(responseData.general));
            } else {
                setErrorMessage(t('environment.deletion.start_failed'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Determine which view the modal shows
    const isDeleting = status?.deletion_status === 'deleting' && status.is_running;
    const isDeleted = status?.deletion_status === 'deleted';
    const isStuck = status?.deletion_status === 'deleting' && !status.is_running;
    const isFailed = status?.deletion_status === 'failed';

    // Render the progress view of a running deletion
    const renderProgress = () => (
        <div className='deletion-progress-block'>
            <p className='text-center'>{t('environment.deletion.progress_label', { progress: status?.deletion_progress ?? 0 })}</p>
            <div className='deletion-progress'>
                <div className='deletion-progress-bar' style={{ width: `${status?.deletion_progress ?? 0}%` }} />
            </div>
            <div className='button-row'>
                <SecondaryButton
                    onClick={() => closeModal()}
                    label='general.close'
                    size='small'
                />
            </div>
        </div>
    );

    // Render the finished view of a deleted environment
    const renderDeleted = () => (
        <div className='deletion-progress-block'>
            <p className='text-center'>{t('environment.deletion.deleted_message')}</p>
            <div className='button-row'>
                <SecondaryButton
                    onClick={() => closeModal()}
                    label='general.close'
                    size='small'
                />
            </div>
        </div>
    );

    // Render the confirmation form with the typed environment name
    const renderConfirmation = () => (
        <>
            <p className='text-center'>
                {t('environment.deletion.modal_message')}
            </p>

            {/* Explain that a failed or stuck deletion continues where it stopped */}
            {(isFailed || isStuck) && (
                <p className='text-center restart-note'>
                    {t(isFailed ? 'environment.deletion.failed_message' : 'environment.deletion.stuck_message')}
                </p>
            )}

            {/* The environment name must be typed to enable the delete button */}
            <label htmlFor='confirmation_name'>
                {t('environment.deletion.confirmation_label')}
            </label>
            <input
                type='text'
                id='confirmation_name'
                name='confirmation_name'
                className='text'
                value={confirmationName}
                placeholder={t('environment.deletion.confirmation_placeholder')}
                onChange={(event) => setConfirmationName(event.target.value)}
                autoFocus
                data-1p-ignore='true'
            />

            {/* Show the error of the backend */}
            {errorMessage && (
                <p className='error-message'>{errorMessage}</p>
            )}

            <div className='button-row'>
                <SecondaryButton
                    onClick={() => closeModal()}
                    label='general.cancel'
                    size='small'
                />
                <PrimaryButton
                    onClick={handleDelete}
                    label='environment.deletion.confirm_button'
                    customClass='warning'
                    size='small'
                    loading={loading}
                    disabled={{ disabled: !nameMatches }}
                />
            </div>
        </>
    );

    return (
        <div className='warning-modal environment-delete-modal'>
            <div className='warning-icon'>
                <FontAwesomeIcon icon={faTriangleExclamation} />
            </div>
            <h3 className='header text-center'>
                {t('environment.deletion.modal_title', { name: environmentName })}
            </h3>

            {/* Show the progress, the finished state or the confirmation form */}
            {status && (isDeleting ? renderProgress() : isDeleted ? renderDeleted() : renderConfirmation())}
        </div>
    );
};

export default EnvironmentDeleteModal;
